import React, { useRef, useState, useEffect } from 'react';
import {
  VegaModal,
  VegaFlex,
  VegaGrid,
  VegaInputSelect,
  VegaButton,
  VegaForm
} from '@heartlandone/vega-react';
import PropTypes from 'prop-types';
import { updateUserRoleAdd } from 'Chores/request';

const UserProfileAddAndUpdateAccessPermissionModal = ({ open = false, onClose = () => {}, projects, userInfo, projectRolesDataSource, fetchUserRolePermission, projectRolePermissions }) => {
  const accessPermissionModalForm = useRef();
  const submitButtonRef = useRef(null);
  const [isEdit, setIsEdit] = useState({});
  const [selectPermissionRoleId, setPermissionSelectRoleId] = useState('');
  const [selectPermissionProjectId, setPermissionSelectProjectId] = useState('');
  const [filteredProjectRoles, setFilteredProjectRoles] = useState({});
  const [addPermissionDisabled, setAddPermissionDisabled] = useState(false);

  useEffect(() => {
    if (!open) {
      setPermissionSelectProjectId('');
    }

    const assignedRolesByProject = projectRolePermissions.reduce((acc, permission) => {
      if (!permission?.projectId || !permission?.roleId) {
        return acc;
      }
      if (!acc[permission.projectId]) {
        acc[permission.projectId] = new Set();
      }
      acc[permission.projectId].add(permission.roleId);
      return acc;
    }, {});

    const nextFilteredRoles = Object.keys(projectRolesDataSource || {}).reduce((acc, projectId) => {
      const roles = Array.isArray(projectRolesDataSource[projectId]) ? projectRolesDataSource[projectId] : [];
      const assigned = assignedRolesByProject[projectId];
      if (!assigned) {
        acc[projectId] = roles;
        return acc;
      }
      acc[projectId] = roles.filter((role) => role && !assigned.has(role.id ?? role.roleId));
      return acc;
    }, {});

    setFilteredProjectRoles(nextFilteredRoles);

    const activeRoles = nextFilteredRoles[selectPermissionProjectId] || [];
    const selectionStillValid = activeRoles.some((role) => role?.id === selectPermissionRoleId || role?.roleId === selectPermissionRoleId);
    if (selectPermissionRoleId && !selectionStillValid) {
      setPermissionSelectRoleId('');
    }
  }, [open, projects, projectRolePermissions, projectRolesDataSource, selectPermissionProjectId, selectPermissionRoleId]);

  const onAddPermission = () => {
    setAddPermissionDisabled(true);
    const existUserRole = projectRolePermissions.filter((item) => {
      return item.projectId === selectPermissionProjectId && item.roleId === selectPermissionRoleId;
    });

    if (existUserRole.length > 0) {
      VegaNotify.open({
        type: 'error',
        message: 'The specified role has been assigned to the current user.'
      });

      setAddPermissionDisabled(false);
      return;
    }

    const params = {
      'userId': [
        userInfo.userId
      ],
      'moduleRole': [
      ],
      'projectRole': [
        {
          'projectId': selectPermissionProjectId,
          'roleId': selectPermissionRoleId
        }
      ]
    };

    updateUserRoleAdd(params).then((res) => {
      VegaNotify.open({
        type: 'success',
        message: 'The permission has been added.'
      });
      fetchUserRolePermission();
      closeModal();
    })
      .catch((error) => {
        VegaNotify.open({
          type: 'error',
          message: 'The permission could not be added.'
        });
      })
      .finally(() => {
        setAddPermissionDisabled(false);
      });
  };

  const save = async () => {
    accessPermissionModalForm.current.valid().then(async (res) => {
      if (!res.isValid) {
        return;
      }

      onAddPermission();
    });
  };

  const closeModal = () => {
    setIsEdit(false);
    setPermissionSelectProjectId('');
    setPermissionSelectRoleId('');
    onClose();
  };

  const onChangeProject = (projectId) => {
    if (projectId) {
      setPermissionSelectProjectId(projectId);
    }
  };

  const onChangeSelectPermissionRoleId = (selectPermissionRoleId) => {
    if (selectPermissionRoleId) {
      setPermissionSelectRoleId(selectPermissionRoleId);
    }
  };

  return (
    <VegaModal
      open={open}
      id='UserProfileAddAndUpdateAccessPermissionModal'
      size={'800'}
      modalTitle={(isEdit ? 'Edit' : 'Add').concat(` Access Permission`)}
      padding="size-24"
      backdrop={'static'}
      onVegaClose={closeModal}
    >
      <VegaForm
        ref={accessPermissionModalForm}
        data-vega-form="addOrEditItem"
      >
        <VegaGrid column="1" row="2" gap="size-16">
          <VegaInputSelect
            label={'Project Name'}
            placeholder={'Select Project'}
            size={'small'}
            required="true"
            source={[...(projects || [])].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))}
            value={selectPermissionProjectId}
            vegaDropdownProps={{ searchable: true }}
            data-vega-form="projectId"
            autoValidation={false}
            onVegaChange={(e) => onChangeProject(e.target.value)}
          >
          </VegaInputSelect>
          <VegaInputSelect
            id="permission"
            name="permission"
            label="Permission"
            max-height="100"
            required="true"
            value={selectPermissionRoleId}
            source={filteredProjectRoles[selectPermissionProjectId] || []}
            size={'small'}
            onVegaChange={(e) => onChangeSelectPermissionRoleId(e.target.value)}
            vegaDropdownProps={{ searchable: false }}
          ></VegaInputSelect>
        </VegaGrid>
      </VegaForm>
`     <div className='v-pt-size-4 v-pb-size-2 v--mr-size-16'>
        <VegaFlex
          direction={'row'}
          gap={'size-12'}
          margin={'size-16'}
          justifyContent={'end'}
        >
          <VegaButton
            label={'Cancel'}
            size={'small'}
            variant={'secondary'}
            onVegaClick={closeModal}
          ></VegaButton>
          <VegaButton
            label={'Submit'}
            size={'small'}
            ref={submitButtonRef}
            disabled={addPermissionDisabled}
            onVegaClick={() => {
              save();
            }} ></VegaButton>
        </VegaFlex>`
      </div>
    </VegaModal>
  );
};

export default UserProfileAddAndUpdateAccessPermissionModal;

UserProfileAddAndUpdateAccessPermissionModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  projects: PropTypes.array,
  userInfo: PropTypes.object,
  fetchUserRolePermission: PropTypes.func,
  projectRolesDataSource: PropTypes.object,
  projectRolePermissions: PropTypes.array
};
